import { useState } from 'react';
import { Editor } from '@/components/Editor';

/**
 * Challenge data for the form.
 */
export interface Challenge {
    stat_type: string;
    difficulty: number;
}

/**
 * Reward data for the form.
 */
export interface Reward {
    reward_type: 'item' | 'blueprint' | 'material' | 'xp' | 'reputation';
    item_recipe_id: number | null;
    item_rarity: string | null;
    material_type: string | null;
    enclave_id: number | null;
    reputation_min: number | null;
    reputation_max: number | null;
    quantity_min: number;
    quantity_max: number;
    chance_percent: number;
    tags: string[];
}

/**
 * Stage data for the form.
 */
export interface Stage {
    stage_number: number;
    dialogue_pre: string;
    dialogue_success: string;
    dialogue_failure: string;
    challenges: Challenge[];
    rewards: Reward[];
}

/**
 * Item recipe option.
 */
interface ItemRecipeOption {
    id: number;
    name: string;
    rarity: string;
    learnable_by: 'no' | 'blueprint' | 'building';
}

/**
 * Tag option.
 */
interface TagOption {
    id: number;
    name: string;
}

/**
 * Enclave option.
 */
interface EnclaveOption {
    id: number;
    name: string;
    owner_name: string | null;
}

/**
 * Rarity option.
 */
interface RarityOption {
    value: string;
    label: string;
}

/**
 * Reward type option.
 */
interface RewardTypeOption {
    value: string;
    label: string;
}

/**
 * Predefined material types for rewards.
 */
const MATERIAL_TYPES = [
    { value: 'tea', label: '🍵 Tea' },
    { value: 'wood', label: '🪵 Wood' },
    { value: 'mystical', label: '✨ Mystical' },
    { value: 'medicinal', label: '💊 Medicinal' },
    { value: 'edible', label: '🍎 Edible' },
];

interface StageEditorProps {
    stage: Stage;
    stageIndex: number;
    totalStages: number;
    allStatTypes: { value: string; label: string; type: string }[];
    rewardTypes: RewardTypeOption[];
    rarities: RarityOption[];
    itemRecipes: ItemRecipeOption[];
    allTags: TagOption[];
    enclaves: EnclaveOption[];
    onUpdate: (index: number, field: keyof Stage, value: unknown) => void;
    onRemove: (index: number) => void;
    onMoveUp: (index: number) => void;
    onMoveDown: (index: number) => void;
}

/**
 * StageEditor component for editing a single stage in a multi-stage adventure.
 */
export function StageEditor({
    stage,
    stageIndex,
    totalStages,
    allStatTypes,
    rewardTypes,
    rarities,
    itemRecipes,
    allTags,
    enclaves,
    onUpdate,
    onRemove,
    onMoveUp,
    onMoveDown,
}: StageEditorProps) {
    const [isExpanded, setIsExpanded] = useState(true);

    // Challenge handlers for this stage
    const addChallenge = () => {
        const newChallenges = [...stage.challenges, { stat_type: allStatTypes[0]?.value || '', difficulty: 10 }];
        onUpdate(stageIndex, 'challenges', newChallenges);
    };

    const removeChallenge = (challengeIndex: number) => {
        const newChallenges = stage.challenges.filter((_, i) => i !== challengeIndex);
        onUpdate(stageIndex, 'challenges', newChallenges);
    };

    const updateChallenge = (challengeIndex: number, field: keyof Challenge, value: string | number) => {
        const newChallenges = [...stage.challenges];
        newChallenges[challengeIndex] = { ...newChallenges[challengeIndex], [field]: value };
        onUpdate(stageIndex, 'challenges', newChallenges);
    };

    // Reward handlers for this stage
    const addReward = () => {
        const newRewards = [...stage.rewards, {
            reward_type: 'item' as const,
            item_recipe_id: null,
            item_rarity: null,
            material_type: null,
            enclave_id: null,
            reputation_min: null,
            reputation_max: null,
            quantity_min: 1,
            quantity_max: 1,
            chance_percent: 100,
            tags: [],
        }];
        onUpdate(stageIndex, 'rewards', newRewards);
    };

    const removeReward = (rewardIndex: number) => {
        const newRewards = stage.rewards.filter((_, i) => i !== rewardIndex);
        onUpdate(stageIndex, 'rewards', newRewards);
    };

    const updateReward = (rewardIndex: number, field: keyof Reward, value: unknown) => {
        const newRewards = [...stage.rewards];
        newRewards[rewardIndex] = { ...newRewards[rewardIndex], [field]: value };
        // Auto-set max to min when min changes
        if (field === 'quantity_min' && typeof value === 'number') {
            newRewards[rewardIndex].quantity_max = value;
        }
        if (field === 'reputation_min' && typeof value === 'number') {
            newRewards[rewardIndex].reputation_max = value;
        }
        onUpdate(stageIndex, 'rewards', newRewards);
    };

    return (
        <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
            {/* Stage header */}
            <div className="flex items-center justify-between p-4 bg-slate-700/50 border-b border-slate-700">
                <button
                    type="button"
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="flex items-center gap-2 text-white font-medium hover:text-purple-300 transition-colors"
                >
                    <span className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>▶</span>
                    <span className="bg-purple-600 text-white text-xs px-2 py-0.5 rounded">
                        Stage {stage.stage_number}
                    </span>
                    <span className="text-gray-400 text-sm">
                        ({stage.challenges.length} challenge{stage.challenges.length !== 1 ? 's' : ''},
                        {' '}{stage.rewards.length} reward{stage.rewards.length !== 1 ? 's' : ''})
                    </span>
                </button>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => onMoveUp(stageIndex)}
                        disabled={stageIndex === 0}
                        className="px-2 py-1 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        title="Move up"
                    >
                        ↑
                    </button>
                    <button
                        type="button"
                        onClick={() => onMoveDown(stageIndex)}
                        disabled={stageIndex === totalStages - 1}
                        className="px-2 py-1 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        title="Move down"
                    >
                        ↓
                    </button>
                    <button
                        type="button"
                        onClick={() => onRemove(stageIndex)}
                        className="px-2 py-1 text-red-400 hover:text-red-300 transition-colors"
                        title="Remove stage"
                    >
                        ✕
                    </button>
                </div>
            </div>

            {/* Stage content (collapsible) */}
            {isExpanded && (
                <div className="p-4 space-y-4">
                    {/* Dialogue fields */}
                    <div className="space-y-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Pre-Stage Dialogue</label>
                            <div data-color-mode="dark">
                                <Editor
                                    value={stage.dialogue_pre}
                                    onChange={(value) => onUpdate(stageIndex, 'dialogue_pre', value)}
                                    placeholder="Text shown before this stage begins..."
                                    height={100}
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-medium text-green-400 mb-1">Success Dialogue</label>
                                <div data-color-mode="dark">
                                    <Editor
                                        value={stage.dialogue_success}
                                        onChange={(value) => onUpdate(stageIndex, 'dialogue_success', value)}
                                        placeholder="Text shown on success..."
                                        height={80}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-red-400 mb-1">Failure Dialogue</label>
                                <div data-color-mode="dark">
                                    <Editor
                                        value={stage.dialogue_failure}
                                        onChange={(value) => onUpdate(stageIndex, 'dialogue_failure', value)}
                                        placeholder="Text shown on failure..."
                                        height={80}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Challenges */}
                    <div className="bg-slate-700/50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-medium text-white">Challenges</h4>
                            <button
                                type="button"
                                onClick={addChallenge}
                                className="px-2 py-1 text-xs bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                            >
                                + Add
                            </button>
                        </div>
                        {stage.challenges.length === 0 ? (
                            <p className="text-gray-500 text-sm">No challenges. Add at least one challenge.</p>
                        ) : (
                            <div className="space-y-2">
                                {stage.challenges.map((challenge, idx) => (
                                    <div key={idx} className="flex items-center gap-3 p-2 bg-slate-600 rounded">
                                        <select
                                            value={challenge.stat_type}
                                            onChange={(e) => updateChallenge(idx, 'stat_type', e.target.value)}
                                            className="flex-1 px-2 py-1 bg-slate-700 border border-slate-500 rounded text-white text-sm"
                                        >
                                            <optgroup label="Stats">
                                                {allStatTypes.filter(s => s.type === 'stat').map(s => (
                                                    <option key={s.value} value={s.value}>{s.label}</option>
                                                ))}
                                            </optgroup>
                                            <optgroup label="Skills">
                                                {allStatTypes.filter(s => s.type === 'skill').map(s => (
                                                    <option key={s.value} value={s.value}>{s.label}</option>
                                                ))}
                                            </optgroup>
                                        </select>
                                        <div className="flex items-center gap-1">
                                            <span className="text-xs text-gray-400">Diff:</span>
                                            <input
                                                type="number"
                                                value={challenge.difficulty}
                                                onChange={(e) => updateChallenge(idx, 'difficulty', parseInt(e.target.value) || 0)}
                                                min={0}
                                                max={100}
                                                className="w-16 px-2 py-1 bg-slate-700 border border-slate-500 rounded text-white text-sm"
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removeChallenge(idx)}
                                            className="text-red-400 hover:text-red-300"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Rewards */}
                    <div className="bg-slate-700/50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-medium text-white">Rewards</h4>
                            <button
                                type="button"
                                onClick={addReward}
                                className="px-2 py-1 text-xs bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                            >
                                + Add
                            </button>
                        </div>
                        {stage.rewards.length === 0 ? (
                            <p className="text-gray-500 text-sm">No rewards for this stage.</p>
                        ) : (
                            <div className="space-y-2">
                                {stage.rewards.map((reward, idx) => (
                                    <StageRewardRow
                                        key={idx}
                                        reward={reward}
                                        index={idx}
                                        rewardTypes={rewardTypes}
                                        rarities={rarities}
                                        itemRecipes={itemRecipes}
                                        allTags={allTags}
                                        enclaves={enclaves}
                                        onUpdate={updateReward}
                                        onRemove={removeReward}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

/**
 * Compact reward row for stage editor.
 */
interface StageRewardRowProps {
    reward: Reward;
    index: number;
    rewardTypes: RewardTypeOption[];
    rarities: RarityOption[];
    itemRecipes: ItemRecipeOption[];
    allTags: TagOption[];
    enclaves: EnclaveOption[];
    onUpdate: (index: number, field: keyof Reward, value: unknown) => void;
    onRemove: (index: number) => void;
}

function StageRewardRow({ reward, index, rewardTypes, rarities, itemRecipes, allTags, enclaves, onUpdate, onRemove }: StageRewardRowProps) {
    const [showTags, setShowTags] = useState(false);

    const toggleTag = (tagName: string) => {
        const newTags = reward.tags.includes(tagName)
            ? reward.tags.filter(t => t !== tagName)
            : [...reward.tags, tagName];
        onUpdate(index, 'tags', newTags);
    };

    return (
        <div className="p-3 bg-slate-600 rounded space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
                <select
                    value={reward.reward_type}
                    onChange={(e) => onUpdate(index, 'reward_type', e.target.value)}
                    className="w-28 px-2 py-1 bg-slate-700 border border-slate-500 rounded text-white text-xs"
                >
                    {rewardTypes.map(rt => (
                        <option key={rt.value} value={rt.value}>{rt.label}</option>
                    ))}
                </select>

                {/* Item/Blueprint specific fields */}
                {(reward.reward_type === 'item' || reward.reward_type === 'blueprint') && (
                    <>
                        <select
                            value={reward.item_recipe_id || ''}
                            onChange={(e) => onUpdate(index, 'item_recipe_id', e.target.value ? parseInt(e.target.value) : null)}
                            className="flex-1 min-w-32 px-2 py-1 bg-slate-700 border border-slate-500 rounded text-white text-xs"
                        >
                            <option value="">Random</option>
                            {(reward.reward_type === 'blueprint'
                                ? itemRecipes.filter(r => r.learnable_by === 'blueprint')
                                : itemRecipes
                            ).map(r => (
                                <option key={r.id} value={r.id}>{r.name}</option>
                            ))}
                        </select>
                        <select
                            value={reward.item_rarity || ''}
                            onChange={(e) => onUpdate(index, 'item_rarity', e.target.value || null)}
                            className="w-24 px-2 py-1 bg-slate-700 border border-slate-500 rounded text-white text-xs"
                        >
                            <option value="">Any</option>
                            {rarities.map(r => (
                                <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                        </select>
                    </>
                )}

                {/* Material specific fields */}
                {reward.reward_type === 'material' && (
                    <>
                        <select
                            value={reward.material_type || ''}
                            onChange={(e) => onUpdate(index, 'material_type', e.target.value || null)}
                            className="flex-1 px-2 py-1 bg-slate-700 border border-slate-500 rounded text-white text-xs"
                        >
                            <option value="">Select...</option>
                            {MATERIAL_TYPES.map(mt => (
                                <option key={mt.value} value={mt.value}>{mt.label}</option>
                            ))}
                        </select>
                        <div className="flex items-center gap-1">
                            <input
                                type="number"
                                value={reward.quantity_min}
                                onChange={(e) => onUpdate(index, 'quantity_min', parseInt(e.target.value) || 1)}
                                min={1}
                                className="w-14 px-1 py-1 bg-slate-700 border border-slate-500 rounded text-white text-xs text-center"
                                placeholder="Min"
                            />
                            <span className="text-gray-400 text-xs">-</span>
                            <input
                                type="number"
                                value={reward.quantity_max}
                                onChange={(e) => onUpdate(index, 'quantity_max', parseInt(e.target.value) || 1)}
                                min={1}
                                className="w-14 px-1 py-1 bg-slate-700 border border-slate-500 rounded text-white text-xs text-center"
                                placeholder="Max"
                            />
                        </div>
                    </>
                )}

                {/* XP specific fields */}
                {reward.reward_type === 'xp' && (
                    <div className="flex items-center gap-1">
                        <input
                            type="number"
                            value={reward.quantity_min}
                            onChange={(e) => onUpdate(index, 'quantity_min', parseInt(e.target.value) || 1)}
                            min={1}
                            className="w-16 px-1 py-1 bg-slate-700 border border-slate-500 rounded text-white text-xs text-center"
                            placeholder="Min"
                        />
                        <span className="text-gray-400 text-xs">-</span>
                        <input
                            type="number"
                            value={reward.quantity_max}
                            onChange={(e) => onUpdate(index, 'quantity_max', parseInt(e.target.value) || 1)}
                            min={1}
                            className="w-16 px-1 py-1 bg-slate-700 border border-slate-500 rounded text-white text-xs text-center"
                            placeholder="Max"
                        />
                        <span className="text-gray-400 text-xs">XP</span>
                    </div>
                )}

                {/* Reputation specific fields */}
                {reward.reward_type === 'reputation' && (
                    <>
                        <select
                            value={reward.enclave_id || ''}
                            onChange={(e) => onUpdate(index, 'enclave_id', e.target.value ? parseInt(e.target.value) : null)}
                            className="flex-1 px-2 py-1 bg-slate-700 border border-slate-500 rounded text-white text-xs"
                        >
                            <option value="">Select enclave...</option>
                            {enclaves.map(e => (
                                <option key={e.id} value={e.id}>{e.owner_name || e.name}</option>
                            ))}
                        </select>
                        <div className="flex items-center gap-1">
                            <input
                                type="number"
                                value={reward.reputation_min ?? ''}
                                onChange={(e) => onUpdate(index, 'reputation_min', e.target.value ? parseInt(e.target.value) : null)}
                                className="w-14 px-1 py-1 bg-slate-700 border border-slate-500 rounded text-white text-xs text-center"
                                placeholder="Min"
                            />
                            <span className="text-gray-400 text-xs">-</span>
                            <input
                                type="number"
                                value={reward.reputation_max ?? ''}
                                onChange={(e) => onUpdate(index, 'reputation_max', e.target.value ? parseInt(e.target.value) : null)}
                                className="w-14 px-1 py-1 bg-slate-700 border border-slate-500 rounded text-white text-xs text-center"
                                placeholder="Max"
                            />
                        </div>
                    </>
                )}

                {/* Chance % */}
                <div className="flex items-center gap-1">
                    <input
                        type="number"
                        value={reward.chance_percent}
                        onChange={(e) => onUpdate(index, 'chance_percent', parseInt(e.target.value) || 100)}
                        min={1}
                        max={100}
                        className="w-12 px-1 py-1 bg-slate-700 border border-slate-500 rounded text-white text-xs text-center"
                    />
                    <span className="text-gray-400 text-xs">%</span>
                </div>

                <button
                    type="button"
                    onClick={() => onRemove(index)}
                    className="text-red-400 hover:text-red-300 text-sm"
                >
                    ✕
                </button>
            </div>

            {/* Tags for random items */}
            {(reward.reward_type === 'item' || reward.reward_type === 'blueprint') && !reward.item_recipe_id && (
                <div>
                    <button
                        type="button"
                        onClick={() => setShowTags(!showTags)}
                        className="text-xs text-purple-400 hover:text-purple-300"
                    >
                        {showTags ? '▼ Hide Tags' : '▶ Filter by Tags'}
                        {reward.tags.length > 0 && ` (${reward.tags.length} selected)`}
                    </button>
                    {showTags && (
                        <div className="flex flex-wrap gap-1 mt-2">
                            {allTags.map(tag => (
                                <button
                                    key={tag.id}
                                    type="button"
                                    onClick={() => toggleTag(tag.name)}
                                    className={`px-2 py-0.5 text-xs rounded transition-colors ${
                                        reward.tags.includes(tag.name)
                                            ? 'bg-purple-600 text-white'
                                            : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                                    }`}
                                >
                                    {tag.name}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default StageEditor;
